import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  X, MapPinArea, Plus, PencilSimple, TrashSimple, MagnifyingGlass,
  CheckCircle, Crosshair, Target, Link, LinkBreak,
} from "@phosphor-icons/react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLORS = {
  "Warehouse":               "#2dd4bf",
  "Distribution Center":     "#06b6d4",
  "Airport Cargo Facility":  "#a78bfa",
  "Port":                    "#60a5fa",
  "Customer Facility":       "#34d399",
  "Hospital":                "#f87171",
  "Pharmacy":                "#fb923c",
  "Origin":                  "#facc15",
  "Destination":             "#84cc16",
  "Custom":                  "#9ca3af",
};
const EMPTY = {
  name: "", type: "Warehouse",
  latitude: 39.5, longitude: -98.35,
  radius_m: 500, notes: "",
};

/**
 * GeofenceManager — circular geofence CRUD with map + form picker.
 * User-defined radius only (NO defaults beyond initial 500m placeholder).
 */
export default function GeofenceManager({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [assigningGeofence, setAssigningGeofence] = useState(null);
  const [shipments, setShipments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [items, meta, ships] = await Promise.all([
        axios.get(`${API}/geofences`),
        axios.get(`${API}/geofences/meta/enums`),
        axios.get(`${API}/shipments/?limit=500`).catch(() => ({ data: [] })),
      ]);
      setItems(items.data.items || []);
      setTypes(meta.data.types || []);
      const sList = Array.isArray(ships.data) ? ships.data : (ships.data?.items || []);
      setShipments(sList.slice(0, 500));
    } catch (e) {
      toast.error("Failed to load geofences");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const filtered = !search ? items : items.filter((g) =>
    [g.name, g.type, g.notes].filter(Boolean).some((v) =>
      String(v).toLowerCase().includes(search.toLowerCase())
    )
  );

  const save = async (payload) => {
    try {
      const body = {
        name: payload.name,
        type: payload.type,
        latitude: parseFloat(payload.latitude),
        longitude: parseFloat(payload.longitude),
        radius_m: parseFloat(payload.radius_m),
        notes: payload.notes || null,
      };
      if (payload.id) {
        await axios.put(`${API}/geofences/${payload.id}`, body);
        toast.success("Geofence updated");
      } else {
        await axios.post(`${API}/geofences`, body);
        toast.success("Geofence created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete geofence "${g.name}"? Any shipments referencing it will be unset.`)) return;
    try {
      await axios.delete(`${API}/geofences/${g.id}`);
      toast.success("Geofence deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[1900] bg-black/70 backdrop-blur-sm flex items-stretch justify-center"
      onClick={onClose}>
      <div className="bg-zinc-950 border-l border-zinc-800 w-full max-w-6xl ml-auto flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="geofence-manager">

        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPinArea size={20} weight="duotone" className="text-teal-400" />
            <div>
              <div className="text-zinc-100 font-semibold flex items-center gap-2">
                Geofences
                <span className="text-[10px] uppercase tracking-wider bg-teal-500/10 text-teal-400 border border-teal-500/30 px-2 py-0.5 rounded-sm">
                  User-Defined
                </span>
              </div>
              <div className="text-xs text-zinc-500">
                Circular geofences for origins, destinations, and operational locations. Radius is set by you — no defaults.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-zinc-500 hidden md:inline">
              Tip: Draw new fences on the <b className="text-teal-400">Map</b> view
            </span>
            <Button size="sm" onClick={() => setEditing({ ...EMPTY })}
              className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs"
              data-testid="geofence-add-btn">
              <Plus size={13} className="mr-1.5" /> Add Geofence
            </Button>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200 ml-2"
              data-testid="geofence-manager-close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="px-6 py-3 border-b border-zinc-800 flex items-center gap-3">
          <div className="relative flex-1 min-w-64">
            <MagnifyingGlass size={14} className="absolute left-3 top-2.5 text-zinc-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, type, or notes…"
              className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-200 h-9 text-sm"
              data-testid="geofence-search" />
          </div>
          <div className="text-xs text-zinc-500 font-mono">
            {filtered.length} / {items.length}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading && <div className="p-8 text-center text-xs text-zinc-500 font-mono">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-12 text-center text-zinc-500">
              <MapPinArea size={40} weight="thin" className="mx-auto mb-3 text-zinc-700" />
              <div className="text-sm">No geofences defined yet.</div>
              <div className="text-xs mt-2">Click "Add Geofence" to draw one on the map.</div>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
              {filtered.map((g) => (
                <Card key={g.id} className="bg-zinc-900 border-zinc-800 p-4"
                  data-testid={`geofence-card-${g.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full inline-block"
                          style={{ backgroundColor: TYPE_COLORS[g.type] || "#9ca3af" }} />
                        {g.name}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{g.type}</div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setAssigningGeofence(g)}
                        className="text-zinc-400 hover:text-teal-300 p-1"
                        title="Assign to shipment"
                        data-testid={`geofence-assign-${g.id}`}>
                        <Link size={14} />
                      </button>
                      <button onClick={() => setEditing({ ...g })}
                        className="text-zinc-400 hover:text-teal-300 p-1"
                        data-testid={`geofence-edit-${g.id}`}>
                        <PencilSimple size={14} />
                      </button>
                      <button onClick={() => remove(g)}
                        className="text-zinc-500 hover:text-red-300 p-1"
                        data-testid={`geofence-delete-${g.id}`}>
                        <TrashSimple size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs">
                    <Row label="Center" value={`${g.latitude.toFixed(5)}, ${g.longitude.toFixed(5)}`} mono />
                    <Row label="Radius" value={formatMeters(g.radius_m)} />
                    {g.notes && <Row label="Notes" value={g.notes} />}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {editing && (
          <GeofenceEditor initial={editing} types={types}
            onCancel={() => setEditing(null)} onSave={save} />
        )}
        {assigningGeofence && (
          <AssignShipmentDialog geofence={assigningGeofence} shipments={shipments}
            onClose={() => setAssigningGeofence(null)} onChange={load} />
        )}
      </div>
    </div>
  );
}

function AssignShipmentDialog({ geofence, shipments, onClose, onChange }) {
  const [linked, setLinked] = useState([]);
  const [shipmentId, setShipmentId] = useState("");
  const [role, setRole] = useState("origin");
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/geofences/${geofence.id}/shipments`);
      setLinked(res.data.items || []);
    } catch {
      setLinked([]);
    }
  }, [geofence.id]);

  useEffect(() => { reload(); }, [reload]);

  const attach = async () => {
    if (!shipmentId) { toast.error("Pick a shipment"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/geofences/assign-to-shipment/${shipmentId}`, {
        role, geofence_id: geofence.id,
      });
      toast.success(`Linked as ${role}`);
      setShipmentId("");
      await reload(); onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    } finally { setLoading(false); }
  };

  const detach = async (sid) => {
    setLoading(true);
    try {
      await axios.post(`${API}/geofences/detach-from-shipment/${sid}/${geofence.id}`);
      toast.success("Detached");
      await reload(); onChange?.();
    } catch (e) {
      toast.error("Detach failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[1950] bg-black/80 flex items-center justify-center p-6"
      onClick={onClose}>
      <Card className="bg-zinc-950 border-zinc-800 w-full max-w-2xl p-6"
        onClick={(e) => e.stopPropagation()} data-testid="assign-shipment-dialog">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-zinc-100 font-semibold text-base flex items-center gap-2">
              <Link size={16} className="text-teal-400" weight="duotone" />
              Shipment Assignments
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">Link <span className="text-zinc-300">{geofence.name}</span> to shipments. Role tagging is optional.</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"
            data-testid="assign-dialog-close"><X size={18} /></button>
        </div>

        {/* Add new */}
        <div className="grid grid-cols-12 gap-2 items-end p-3 bg-zinc-900/60 border border-zinc-800 rounded-md mb-4">
          <div className="col-span-6">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Shipment</Label>
            <select value={shipmentId} onChange={(e) => setShipmentId(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 text-zinc-200 text-sm rounded-md px-3 py-2 w-full"
              data-testid="assign-shipment-picker">
              <option value="">— Select a shipment —</option>
              {shipments.map((s) => (
                <option key={s.id} value={s.id}>{(s.name || s.id || "").slice(0, 60)} — {s.status}</option>
              ))}
            </select>
          </div>
          <div className="col-span-4">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Role</Label>
            <select value={role} onChange={(e) => setRole(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 text-zinc-200 text-sm rounded-md px-3 py-2 w-full"
              data-testid="assign-shipment-role">
              <option value="origin">Origin</option>
              <option value="destination">Destination</option>
              <option value="waypoint">Waypoint</option>
            </select>
          </div>
          <div className="col-span-2">
            <Button onClick={attach} disabled={loading || !shipmentId}
              className="bg-teal-500 hover:bg-teal-400 text-zinc-950 w-full"
              data-testid="assign-shipment-link-btn">
              <Link size={13} weight="bold" className="mr-1" /> Link
            </Button>
          </div>
        </div>

        {/* Linked list */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">
            Linked ({linked.length})
          </Label>
          {linked.length === 0 && (
            <div className="text-xs text-zinc-500 py-4 text-center font-mono border border-dashed border-zinc-800 rounded-md">
              No shipments linked yet.
            </div>
          )}
          {linked.map((s) => (
            <div key={s.id + s.role} className="flex items-center justify-between py-2 px-3 border-b border-zinc-900 hover:bg-zinc-900/40"
              data-testid={`linked-shipment-${s.id}-${s.role}`}>
              <div className="flex-1">
                <div className="text-sm text-zinc-200">{s.name || s.id}</div>
                <div className="text-[10px] text-zinc-500 font-mono uppercase">{s.status}</div>
              </div>
              <span className={`text-[10px] uppercase px-2 py-0.5 rounded-sm border mr-3 ${
                s.role === "origin" ? "bg-amber-500/10 text-amber-300 border-amber-500/30" :
                s.role === "destination" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" :
                "bg-sky-500/10 text-sky-300 border-sky-500/30"
              }`}>
                {s.role}
              </span>
              <button onClick={() => detach(s.id)} disabled={loading}
                className="text-zinc-500 hover:text-red-300 p-1"
                title="Detach"
                data-testid={`detach-shipment-${s.id}`}>
                <LinkBreak size={14} />
              </button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-zinc-500 w-16">{label}</span>
      <span className={`text-zinc-300 ${mono ? "font-mono" : ""} truncate`}>{value}</span>
    </div>
  );
}

function formatMeters(m) {
  if (m >= 1000) return `${(m / 1000).toFixed(m % 1000 === 0 ? 0 : 2)} km`;
  return `${Math.round(m)} m`;
}

/** Map + form editor. Click on map to drop pin; radius slider re-renders circle. */
function GeofenceEditor({ initial, types, onCancel, onSave }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const isNew = !initial.id;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Init map once
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    if (mapRef.current._leaflet_id) {
      mapRef.current._leaflet_id = null;
    }
    const map = L.map(mapRef.current, {
      center: [form.latitude || 39.5, form.longitude || -98.35],
      zoom: initial.id ? 12 : 4,
      zoomControl: true,
      preferCanvas: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; OpenStreetMap contributors',
      subdomains: "abcd", maxZoom: 19,
    }).addTo(map);

    // Click to set position
    map.on("click", (e) => {
      update("latitude", e.latlng.lat);
      update("longitude", e.latlng.lng);
    });
    mapInstance.current = map;

    // Cleanup
    return () => {
      try { map.remove(); } catch {}
      mapInstance.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-render marker + circle when lat/lng/radius change
  useEffect(() => {
    if (!mapInstance.current) return;
    const map = mapInstance.current;
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    const r = parseFloat(form.radius_m);
    if (isNaN(lat) || isNaN(lng)) return;

    const color = TYPE_COLORS[form.type] || "#2dd4bf";
    if (!markerRef.current) {
      markerRef.current = L.marker([lat, lng], {
        draggable: true,
        icon: L.divIcon({
          className: "geofence-pin",
          html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:2px solid #0a0a0a;box-shadow:0 0 0 2px ${color}77"></div>`,
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        }),
      }).addTo(map);
      markerRef.current.on("dragend", (e) => {
        const ll = e.target.getLatLng();
        update("latitude", ll.lat);
        update("longitude", ll.lng);
      });
    } else {
      markerRef.current.setLatLng([lat, lng]);
      markerRef.current.setIcon(L.divIcon({
        className: "geofence-pin",
        html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:2px solid #0a0a0a;box-shadow:0 0 0 2px ${color}77"></div>`,
        iconSize: [14, 14], iconAnchor: [7, 7],
      }));
    }
    if (circleRef.current) circleRef.current.remove();
    if (!isNaN(r) && r > 0) {
      circleRef.current = L.circle([lat, lng], {
        radius: r, color, weight: 2, fillColor: color, fillOpacity: 0.12,
      }).addTo(map);
    }
  }, [form.latitude, form.longitude, form.radius_m, form.type]);

  const recenter = () => {
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (!isNaN(lat) && !isNaN(lng) && mapInstance.current) {
      mapInstance.current.setView([lat, lng], 13);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        update("latitude", p.coords.latitude);
        update("longitude", p.coords.longitude);
        if (mapInstance.current) mapInstance.current.setView([p.coords.latitude, p.coords.longitude], 14);
      },
      () => toast.error("Could not get your location")
    );
  };

  return (
    <div className="fixed inset-0 z-[1950] bg-black/80 flex items-center justify-center p-4"
      onClick={onCancel}>
      <Card className="bg-zinc-950 border-zinc-800 w-full max-w-5xl flex flex-col"
        style={{ maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()} data-testid="geofence-editor">

        <div className="flex items-center justify-between p-5 border-b border-zinc-900">
          <h3 className="text-zinc-100 font-semibold text-base">
            {isNew ? "Add Geofence" : `Edit · ${initial.name}`}
          </h3>
          <button onClick={onCancel} className="text-zinc-500 hover:text-zinc-200" data-testid="geofence-editor-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-0 flex-1 min-h-0">
          {/* Form column */}
          <div className="col-span-1 p-5 space-y-4 overflow-auto border-r border-zinc-900">
            <Field label="Name *">
              <Input value={form.name} onChange={(e) => update("name", e.target.value)}
                placeholder="Memphis Distribution Center"
                className="bg-zinc-900 border-zinc-800 text-zinc-200"
                data-testid="geofence-form-name" />
            </Field>
            <Field label="Type">
              <select value={form.type} onChange={(e) => update("type", e.target.value)}
                className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md px-3 py-2 w-full"
                data-testid="geofence-form-type">
                {(types.length ? types : Object.keys(TYPE_COLORS)).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>

            <div className="grid grid-cols-2 gap-2">
              <Field label="Latitude">
                <Input type="number" step="0.00001" value={form.latitude}
                  onChange={(e) => update("latitude", e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-zinc-200 font-mono"
                  data-testid="geofence-form-lat" />
              </Field>
              <Field label="Longitude">
                <Input type="number" step="0.00001" value={form.longitude}
                  onChange={(e) => update("longitude", e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-zinc-200 font-mono"
                  data-testid="geofence-form-lng" />
              </Field>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={recenter}
                className="border-zinc-700 hover:bg-zinc-800 text-xs flex-1"
                data-testid="geofence-form-recenter">
                <Target size={12} className="mr-1.5" /> Recenter
              </Button>
              <Button variant="outline" size="sm" onClick={useMyLocation}
                className="border-zinc-700 hover:bg-zinc-800 text-xs flex-1"
                data-testid="geofence-form-my-location">
                <Crosshair size={12} className="mr-1.5" /> My Location
              </Button>
            </div>

            <Field label={`Radius — ${formatMeters(parseFloat(form.radius_m) || 0)}`}>
              <input type="range" min="50" max="20000" step="50"
                value={form.radius_m}
                onChange={(e) => update("radius_m", e.target.value)}
                className="w-full accent-teal-500"
                data-testid="geofence-form-radius-slider" />
              <Input type="number" step="10" value={form.radius_m}
                onChange={(e) => update("radius_m", e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-zinc-200 font-mono mt-2"
                data-testid="geofence-form-radius" />
              <div className="text-[10px] text-zinc-500 mt-1">Meters. No default — set per location.</div>
            </Field>

            <Field label="Notes">
              <Textarea value={form.notes || ""} onChange={(e) => update("notes", e.target.value)}
                rows={3} placeholder="Operating hours, contact, dock numbers…"
                className="bg-zinc-900 border-zinc-800 text-zinc-200"
                data-testid="geofence-form-notes" />
            </Field>
          </div>

          {/* Map column */}
          <div className="col-span-2 relative bg-zinc-900">
            <div ref={mapRef} style={{ height: "100%", minHeight: 360, width: "100%" }}
              data-testid="geofence-map" />
            <div className="absolute top-3 left-3 bg-zinc-950/90 backdrop-blur border border-zinc-800
              text-[10px] text-zinc-400 px-2.5 py-1.5 rounded-sm pointer-events-none">
              Click map to set center · drag pin to reposition · slider sets radius
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-zinc-900">
          <Button variant="outline" onClick={onCancel}
            className="border-zinc-700 hover:bg-zinc-800" data-testid="geofence-form-cancel">
            Cancel
          </Button>
          <Button onClick={() => {
            if (!form.name?.trim()) { toast.error("Name is required"); return; }
            const r = parseFloat(form.radius_m);
            if (!r || r <= 0) { toast.error("Radius must be a positive number"); return; }
            onSave(form);
          }} className="bg-teal-500 hover:bg-teal-400 text-zinc-950" data-testid="geofence-form-save">
            <CheckCircle size={14} weight="bold" className="mr-1.5" />
            {isNew ? "Create Geofence" : "Save Changes"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
